import { z } from 'zod'

import {
  civilDateSchema,
  planMasterBgInputSchema,
  planMasterCodeInputSchema,
  planMasterRowValidFromRefine,
  validPeriodRefine,
  validPeriodRefineIssue,
} from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/**
 * 発電所 (= plants[] の 1 エントリ) 入力 schema。
 * gen-sales-plant-master-select 設計書: 発電所マスタ UUID 参照
 * (plant_master_id) に置換 (AC-07: 未選択時「発電所を選択してください」)。
 */
const planMasterPlantInputSchema = z.object({
  /** 発電所マスタ UUID */
  plantMasterId: z.string().min(1, '発電所を選択してください'),
  /** 適用開始日 */
  validFrom: civilDateSchema,
  // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
  // clearAsNull JSDoc 参照)。`null` を許容し `undefined` へ正規化する。
  /** 適用終了日 (任意) */
  validTo: civilDateSchema
    .nullable()
    .transform((v) => v ?? undefined)
    .optional(),
})

export type PlanMasterPlantInputValues = z.infer<
  typeof planMasterPlantInputSchema
>

/**
 * 発電販売計画マスタ Create / Edit 共通 Zod schema。
 *
 * Create では submitterMasterId / submitterCode (new) を指定する。
 * Update では id のみで識別し submitterCode / submitterMasterId は変更不可。
 * codes[] / bgs[] / plants[] は full replacement (PUT セマンティクス)。
 */
export const generationSalesMasterFormSchema = z
  .object({
    /** 計画提出者マスタ UUID (Create のみ使用) */
    submitterMasterId: z.string().min(1, '計画提出者を選択してください'),
    /** OCCTO 5 桁計画提出者コード (Read-only / Create 時スナップショット) */
    submitterCode: z.string().min(1, '計画提出者コードは必須です'),
    /**
     * 備考 (任意)。 proto CEL `max_len 100` 新設 (Create/Update Request の
     * note field のみ、AC-11、3938-occto-master-plan-spec-followup 設計書 §5)。
     */
    note: z.string().max(100, '備考は100文字以内で入力してください').optional(),
    /**
     * 取引相手コードリスト。 1 件以上必須 (AC-01、spec AC-OMG-002)。
     */
    codes: z
      .array(planMasterCodeInputSchema)
      .min(1, '取引先を1件以上選択してください'),
    /** 所属 BG リスト */
    bgs: z.array(planMasterBgInputSchema),
    /** 発電所リスト */
    plants: z.array(planMasterPlantInputSchema),
    /** 適用開始日 (必須) */
    validFrom: civilDateSchema,
    /** 適用終了日 (任意) */
    validTo: civilDateSchema.optional(),
  })
  .refine(validPeriodRefine, validPeriodRefineIssue)
  .superRefine((value, ctx) => {
    planMasterRowValidFromRefine(
      value.validFrom,
      value.bgs,
      '発電 BG の適用開始日はレコードの適用開始日以降の日付で入力してください',
      'bgs',
      ctx,
    )
    planMasterRowValidFromRefine(
      value.validFrom,
      value.plants,
      '発電所の適用開始日はレコードの適用開始日以降の日付で入力してください',
      'plants',
      ctx,
    )
    planMasterRowValidFromRefine(
      value.validFrom,
      value.codes,
      '取引先の適用開始日はレコードの適用開始日以降の日付で入力してください',
      'codes',
      ctx,
    )
  })

export type GenerationSalesMasterFormValues = z.infer<
  typeof generationSalesMasterFormSchema
>
